import { Box, Button, styled, Typography, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ExecutionReportsTestSuiteTable, SuiteReportSelection } from "./Reports";
import { IconContainer, PageTitle } from "../components-new/common";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabled";
import { RefreshIcon } from "../components-new/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNotification } from "../hooks";
import {
  useProductQuery,
  useTestSuiteResultQuery,
  useStopTestSuiteQuery,
  usePauseResumeTestSuiteMutation,
} from "../useQuery";
import {
  setSelectedProduct,
  setSelectedTestSuite,
  resetExecutionReport,
} from "../Redux/ExecutionReport/ExecutionReportSlice";
import { useLocation, useNavigate } from "react-router-dom";
import Stop from "@mui/icons-material/Stop";
import CustomDialog from "../components-new/common/CustomDialog";

const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const SuiteExecutionReports = () => {
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [testSuiteName, setTestSuiteName] = useState("");
  const [openStopDialog, setOpenStopDialog] = useState(false);
  const [pageNO, setPageno] = useState(1);
  const [showLatestRun, setShowLatestRun] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [runByFilter, setRunByFilter] = useState([]);
  const [runByOptions, setRunByOptions] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [isPrettyView, setIsPrettyView] = useState(true);

  const dispatch = useDispatch();
  const RouteState = useLocation();
  const navigate = useNavigate();
  const notify = useNotification();

  const selectedProduct = useSelector((state) => state.executionSlice.selectedProduct);
  const selectedTestSuite = useSelector((state) => state.executionSlice.selectedTestSuite);

  const { data: products, refetch: refetchProductQuery } = useProductQuery();

  const {
    data: testSuitesResults,
    refetch: fetchTestSuitesResult,
    isSuccess: isGetTestSuiteResultSuccess,
    isLoading: isLoadingSuiteResults,
    isFetching: isFetchingSuiteResults,
    refetch: refetchTestSuiteResults,
  } = useTestSuiteResultQuery(selectedTestSuite, "", pageNO, {
    enabled: selectedTestSuite !== "",
    refetchInterval: refreshFlag ? 5000 : false,
    statusFilter,
    runByFilter,
    dateFilter,
  });

  useEffect(() => {
    const opts = testSuitesResults?.filterOptions?.runBy;
    if (Array.isArray(opts) && opts.length > 0) {
      setRunByOptions(opts);
    }
  }, [testSuitesResults]);

  const { refetch: handleStopTSExecution } = useStopTestSuiteQuery(
    testSuitesResults?.results?.[0]?.idtest_suite_results,
    "suite",
    {
      onUnauthorized: () => {
        notify("You are not authorized to stop a test suite started by another user.", "error");
      },
    },
  );

  const { mutate: pauseResumeTestSuite } = usePauseResumeTestSuiteMutation();

  // Initialize from route state (when navigating from TestSuite page)
  useEffect(() => {
    if (RouteState?.state?.fetchTestSuite) {
      setRefreshFlag(true);
      dispatch(setSelectedProduct(RouteState.state.product));
      const suiteId = RouteState.state.selectedSuit;
      dispatch(setSelectedTestSuite(suiteId));
      if (suiteId) {
        fetchTestSuitesResult(suiteId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RouteState?.state?.fetchTestSuite]);

  // Polling for suite execution status
  useEffect(() => {
    if (selectedTestSuite && testSuitesResults?.results?.length) {
      const firstResultStatus = testSuitesResults.results[0]?.Status;
      const isTestSuiteRunning = firstResultStatus === "STARTED" || firstResultStatus === "RUNNING";

      if (isTestSuiteRunning) {
        setRefreshFlag(true);
      } else {
        setRefreshFlag(false);
      }
    }
  }, [selectedTestSuite, testSuitesResults?.results]);

  useEffect(() => {
    setStatusFilter([]);
    setRunByFilter([]);
    setDateFilter("");
  }, [selectedTestSuite]);

  // Fetch products on mount
  useEffect(() => {
    if (!products || products.length === 0) {
      refetchProductQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(resetExecutionReport());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmStopExecution = () => {
    handleStopTSExecution();
    setOpenStopDialog(false);
    // Refresh results after stopping
    setTimeout(() => {
      fetchTestSuitesResult(selectedTestSuite);
    }, 1000);
  };

  const handleCancelStopExecution = () => {
    setOpenStopDialog(false);
  };

  const removeheader = () => {
    setShowLatestRun(false);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPageno(1);
  };

  const handleRunByFilterChange = (value) => {
    setRunByFilter(value);
    setPageno(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setPageno(1);
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    setRunByFilter([]);
    setDateFilter("");
    setPageno(1);
  };

  const isRunning =
    testSuitesResults?.results?.[0]?.Status === "STARTED" ||
    testSuitesResults?.results?.[0]?.Status === "RUNNING";
  const suiteResultRows = testSuitesResults?.results ?? [];
  const totalPages = Number(testSuitesResults?.totalPages) || 1;

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Running indicator and stop button */}
      {isRunning && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center" mt={2}>
            <Tooltip title="Stop Execution">
              <StyledButton
                variant="contained"
                onClick={() => setOpenStopDialog(true)}
                color="warning"
                endIcon={<Stop />}
                sx={{
                  backgroundColor: "#ff5336",
                  boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                }}
              >
                Stop
              </StyledButton>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#f0f7ff",
                border: "1px solid #2550d1",
                borderRadius: "8px",
                padding: "12px 20px",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor:
                    testSuitesResults?.results?.[0]?.Status === "RUNNING" ? "#4caf50" : "#ff9800",
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#2B3539",
                }}
              >
                Running: <strong>{testSuiteName || "Test Suite"}</strong>
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#8089A8",
                  backgroundColor: "#e3f2fd",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}
              >
                {testSuitesResults?.results?.[0]?.Status}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box>
        <Box
          sx={{
            position: "sticky",
            top: "15px",
            zIndex: 100,
            backgroundColor: "#fff",
            pb: 1,
            pt: 1,
            borderBottom: "1px solid #eee",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent={"space-between"}>
            <Box display="flex" columnGap={3} alignItems={"center"}>
              <SuiteReportSelection
                products={products}
                setTestSuiteName={setTestSuiteName}
                refetchTestSuiteResults={refetchTestSuiteResults}
                setPageno={setPageno}
                setRefreshFlag={setRefreshFlag}
                removeheader={removeheader}
              />
            </Box>

            {selectedTestSuite && (
              <Box display={"flex"} columnGap={"1rem"} alignItems="center">
                <Box>
                  {refreshFlag ? (
                    <IconContainer
                      handleClick={() => {
                        setRefreshFlag(false);
                      }}
                      tooltipText="Stop Refresh"
                    >
                      <SyncDisabledIcon />
                    </IconContainer>
                  ) : (
                    <IconContainer
                      handleClick={() => {
                        setRefreshFlag(true);
                        fetchTestSuitesResult(selectedTestSuite);
                      }}
                      tooltipText="Refresh"
                    >
                      <RefreshIcon />
                    </IconContainer>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {showLatestRun && suiteResultRows.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{ mb: 0.1, mt: 2, fontWeight: "bold", color: "black" }}
            >
              Latest Executed Run Data
            </Typography>
          </Box>
        )}

        {selectedTestSuite ? (
          <Box display="flex" mt={3}>
            <ExecutionReportsTestSuiteTable
              setPageno={setPageno}
              pageNO={pageNO}
              totalPageno={totalPages}
              testSuiteName={testSuiteName}
              data={suiteResultRows}
              isSuccess={isGetTestSuiteResultSuccess}
              prod={products}
              statusFilter={statusFilter}
              runByFilter={runByFilter}
              runByOptions={runByOptions}
              dateFilter={dateFilter}
              onStatusFilterChange={handleStatusFilterChange}
              onRunByFilterChange={handleRunByFilterChange}
              onDateFilterChange={handleDateFilterChange}
              onClearFilters={handleClearFilters}
              isPrettyView={isPrettyView}
              setIsPrettyView={setIsPrettyView}
              isLoadingResults={isLoadingSuiteResults || isFetchingSuiteResults}
            />
          </Box>
        ) : (
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"300px"}>
            Select a product and test suite to view reports
          </Box>
        )}
      </Box>

      <CustomDialog
        open={openStopDialog}
        onClose={handleCancelStopExecution}
        title="Stop Execution"
        content={<Typography>Are you sure you want to stop execution of this Suite?</Typography>}
        actions={
          <>
            <Button onClick={handleConfirmStopExecution}>Yes</Button>
            <Button onClick={handleCancelStopExecution}>No</Button>
          </>
        }
      />
    </Box>
  );
};

export default SuiteExecutionReports;
